import { addressTiersFor } from "@shared/components/WalletMenu/addressTiers";
import { cn } from "@shared/utils/cn";
import { truncateAddress } from "@shared/utils/format";
import {
    CheckIcon,
    PlusIcon,
    RetryIcon
} from "@solomon-labs/design/components/icons";
import {
    Address,
    Button,
    Combobox,
    ComboboxCollection,
    ComboboxContent,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    Field,
    Text,
    textVariants,
    useComboboxAnchor
} from "@solomon-labs/design/components/ui";
import React, { useEffect, useRef, useState } from "react";

import {
    type RememberedSquad,
    useRetailAccountStore
} from "@/hooks/yaas/retailAccountStore";
import { squadsDestinationStatus } from "@/hooks/yaas/retailSquadsPreflight";
import {
    type useRetailAccount,
    useRetailSquadsLookup
} from "@/hooks/yaas/useRetailAccount";
import type { useRetailSquadsDiscovery } from "@/hooks/yaas/useRetailSquadsDiscovery";

/** Inline links in the status row. The DS button label is `leading-none` and
 *  lifted 1px (`-translate-y-[0.0625rem]`), an optical fix for boxed buttons
 *  that puts a text link 1px above the status text's baseline. Give the label
 *  the text's 16/20px line box and drop the lift so both share one baseline. */
const STATUS_LINK_LABEL =
    "[&_[data-slot=button-label]]:leading-[inherit] [&_[data-slot=button-label]]:translate-y-0";

/** List rows read like the field: same `small` type and the same 32px height
 *  (the field is `h-8`, i.e. 6px / 4px around its 20px / 24px line). */
const LIST_ROW = cn(textVariants({ variant: "small" }), "py-1.5 md:py-1");

/** Vault addresses in the field and list are set in `small` (16px from `md`). */
const VAULT_ADDRESS_TIERS = addressTiersFor(16);

/** The list's "enter an address" action. Never a real vault: picking it
 *  clears the field for typing instead of selecting anything. */
const MANUAL_ENTRY: RememberedSquad = {
    address: "manual-entry",
    vaultAddress: ""
};

interface SquadsAccountPanelProps {
    account: ReturnType<typeof useRetailAccount>;
    discovery: ReturnType<typeof useRetailSquadsDiscovery>;
    entries: RememberedSquad[];
    onStopAutoSelect: () => void;
}

export function SquadsAccountPanel({
    account,
    discovery,
    entries,
    onStopAutoSelect
}: SquadsAccountPanelProps): React.ReactElement {
    const store = useRetailAccountStore();
    // Typed/pasted text. `null` means the field shows the selected vault.
    const [draft, setDraft] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const anchor = useComboboxAnchor();
    const input = draft ?? "";
    const [retry, setRetry] = useState(0);
    const lookup = useRetailSquadsLookup(
        input,
        account.signerAddress || null,
        retry
    );
    const gate = squadsDestinationStatus(lookup, true);
    const discovered = discovery.data?.multisigs ?? [];
    const validSigner =
        lookup.status === "success" && lookup.signerStatus === "can-verify";
    const canUseWallet = validSigner && !!lookup.multisig.vaultAddress;

    const selectedGate = squadsDestinationStatus(account.status, true);
    const isAddedVault =
        draft === null &&
        account.isSquads &&
        !discovered.some((entry) => entry.address === account.address);
    // A verified signer with no vault address can't be used (balances and
    // rewards need the vault), so it reads as a failed check, not success.
    const draftMissingVault = validSigner && !canUseWallet;
    // The shared gate treats not-found as fine (an ordinary wallet is a valid
    // rewards destination); here the field only takes Squads multisigs.
    const draftError =
        gate.isError || lookup.status === "not-found" || draftMissingVault;
    const selectedMissingVault =
        account.status.status === "success" &&
        account.status.signerStatus === "can-verify";
    // One status line for whatever the field holds: a typed draft's lookup,
    // or the selected vault's own membership check.
    const status: { tone: "success" | "error" | "muted"; text: string } | null =
        input.trim()
            ? draftMissingVault
                ? { tone: "error", text: "Check failed" }
                : validSigner
                  ? { tone: "success", text: "Valid signer" }
                  : {
                        tone: draftError ? "error" : "muted",
                        text:
                            lookup.status === "not-found"
                                ? "Not a Squads multisig"
                                : lookup.status === "invalid"
                                  ? "Invalid address"
                                  : (gate.message ?? "")
                    }
            : draft === null && account.isSquads
              ? account.ready
                  ? { tone: "success", text: "Valid signer" }
                  : selectedMissingVault
                    ? { tone: "error", text: "Check failed" }
                    : account.status.status === "not-found"
                      ? { tone: "error", text: "Not a Squads multisig" }
                      : account.notice
                        ? {
                              tone: selectedGate.isError ? "error" : "muted",
                              text: account.notice
                          }
                        : null
              : null;

    const canRetry = input.trim()
        ? gate.canRetry || draftMissingVault
        : draft === null &&
          account.isSquads &&
          !account.ready &&
          // Permissions may have changed since this selection was checked.
          (account.status.status === "success" ||
              (!!account.notice && selectedGate.canRetry));

    const selected = account.isSquads
        ? (entries.find((entry) => entry.address === account.address) ?? null)
        : null;
    // An <input> can only hold a string, so the selected vault is drawn by the
    // DS Address over the (transparent) value until the user starts typing.
    const showSelectedAddress = draft === null && selected !== null;
    const query = input.trim().toLowerCase();
    const matches = query
        ? entries.filter(
              (entry) =>
                  entry.vaultAddress.toLowerCase().includes(query) ||
                  entry.address.toLowerCase().includes(query)
          )
        : entries;
    // Until the user types, the list ends with the manual-entry action.
    const listItems = draft === null ? [...entries, MANUAL_ENTRY] : matches;

    // A pasted address that checks out is added and selected, no submit step.
    const lookupVault =
        lookup.status === "success" ? lookup.multisig.vaultAddress : null;
    const lookupMultisig =
        lookup.status === "success" ? lookup.multisig.address : null;
    const { remember: rememberAccount } = store;
    useEffect(() => {
        if (!canUseWallet || !lookupVault || !lookupMultisig) return;
        rememberAccount(account.signerAddress, {
            address: lookupMultisig,
            vaultAddress: lookupVault
        });
        setDraft(null);
        setOpen(false);
    }, [
        canUseWallet,
        lookupVault,
        lookupMultisig,
        account.signerAddress,
        rememberAccount
    ]);

    return (
        <div className="flex flex-col gap-4 px-4">
            <Field data-invalid={!!input && draftError} className="gap-1">
                <Combobox
                    items={[...entries, MANUAL_ENTRY]}
                    filteredItems={listItems}
                    filter={null}
                    value={selected}
                    onValueChange={(entry: RememberedSquad | null, details) => {
                        // Escape clears a combobox by default; here it should
                        // keep the vault and dismiss the wallet menu instead.
                        if (details.reason === "escape-key") {
                            details.allowPropagation();
                            return;
                        }
                        // Clearing a filter is not an account selection. Keep the
                        // draft until a real option or verified address replaces it.
                        if (!entry) return;
                        onStopAutoSelect();
                        if (entry === MANUAL_ENTRY) {
                            setDraft("");
                            setOpen(false);
                            inputRef.current?.focus();
                            return;
                        }
                        setDraft(null);
                        store.select(account.signerAddress, entry);
                    }}
                    isItemEqualToValue={(first, second) =>
                        first?.address === second?.address
                    }
                    itemToStringLabel={(entry: RememberedSquad) =>
                        truncateAddress(entry.vaultAddress, 4, 4)
                    }
                    inputValue={
                        draft ??
                        (selected
                            ? truncateAddress(selected.vaultAddress, 4, 4)
                            : "")
                    }
                    onInputValueChange={(value, details) => {
                        // Base UI emits input-clear when the suggestions popup
                        // closes without a selection. A pasted address is also
                        // a lookup draft, so only actual input edits may clear it.
                        if (details.reason === "input-change") {
                            onStopAutoSelect();
                            setDraft(value);
                        }
                    }}
                    // A typed draft never opens an empty list over the status.
                    open={open && listItems.length > 0}
                    onOpenChange={setOpen}
                >
                    <div ref={anchor}>
                        <ComboboxInput
                            ref={inputRef}
                            onFocus={(event) => {
                                if (draft === null)
                                    event.currentTarget.select();
                            }}
                            onPaste={(event) => {
                                if (draft !== null) return;
                                // The truncated label is display text, not part
                                // of the next address the user is pasting.
                                event.preventDefault();
                                onStopAutoSelect();
                                setDraft(
                                    event.clipboardData.getData("text/plain")
                                );
                                setOpen(false);
                            }}
                            id="squads-vault"
                            aria-label="Squads vault"
                            size="sm"
                            surface="transparent"
                            state={input && draftError ? "error" : undefined}
                            // ComboboxInput only ships the lightbar rail; this mirrors
                            // the DS Input's `interaction="outline"` box on its shell.
                            className={cn(
                                "border border-line-base before:hidden after:hidden",
                                "transition-colors duration-200 ease-in",
                                "hover:border-charcoal-700 hover:duration-75 hover:ease-out",
                                "has-[:focus-visible]:border-charcoal-500 has-[:focus-visible]:duration-75 has-[:focus-visible]:ease-out",
                                input &&
                                    draftError &&
                                    "border-status-error-400 hover:border-status-error-400 has-[:focus-visible]:border-status-error-400"
                            )}
                            aria-describedby={
                                status ? "squads-check" : undefined
                            }
                            aria-invalid={!!input && draftError}
                            autoComplete="off"
                            spellCheck={false}
                            // Leaving manual entry untouched shows the selection again.
                            onBlur={() => {
                                if (draft === "") setDraft(null);
                            }}
                            placeholder="Enter multisig address"
                            // Typed value matches the placeholder's `small` ladder
                            // (the DS combobox pins the value to 14px on desktop).
                            inputClassName={cn(
                                textVariants({ variant: "small" }),
                                // The DS hides the placeholder on focus; in manual
                                // entry the field is focused and empty, so keep the
                                // prompt visible to say "type here".
                                draft === "" &&
                                    "focus:placeholder:text-foreground-inverse-neutral",
                                showSelectedAddress &&
                                    "text-transparent caret-foreground-base"
                            )}
                        >
                            {showSelectedAddress && (
                                // Over the input's text box: its 10px inline
                                // padding, stopping at the 48px chevron addon.
                                <span
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-y-0 left-0 right-12 flex items-center px-2.5 text-foreground-base"
                                >
                                    <Address
                                        value={selected.vaultAddress}
                                        tiers={VAULT_ADDRESS_TIERS}
                                        textVariant="small"
                                        mono={false}
                                        showTitle={false}
                                        className="flex-1"
                                    />
                                </span>
                            )}
                        </ComboboxInput>
                    </div>
                    <ComboboxContent
                        anchor={anchor}
                        // Tint surface so the list doesn't blend into the Balances
                        // card below. `surface` has no tint option, so the highlight
                        // goes through the DS escape hatch: one olive step darker
                        // than the olive-100 surface keeps the active row visible.
                        className="bg-surface-tint-overlay"
                        style={
                            {
                                "--combobox-highlight":
                                    "rgb(var(--color-olive-200))"
                            } as React.CSSProperties
                        }
                    >
                        <ComboboxList>
                            <ComboboxCollection>
                                {(entry: RememberedSquad) =>
                                    entry === MANUAL_ENTRY ? (
                                        // Same treatment as the DS "Add …" create row.
                                        <ComboboxItem
                                            key={entry.address}
                                            value={entry}
                                            className={cn(
                                                LIST_ROW,
                                                "text-foreground-muted data-highlighted:text-foreground-base"
                                            )}
                                        >
                                            Manually Enter Multisig Address
                                            {/* Same right-hand slot as the
                                                selected row's check. */}
                                            <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
                                                <PlusIcon
                                                    aria-hidden="true"
                                                    className="size-3.5 text-foreground-neutral group-data-highlighted/combobox-item:text-accent-base"
                                                />
                                            </span>
                                        </ComboboxItem>
                                    ) : (
                                        <ComboboxItem
                                            key={entry.address}
                                            value={entry}
                                            className={LIST_ROW}
                                        >
                                            <Address
                                                value={entry.vaultAddress}
                                                tiers={VAULT_ADDRESS_TIERS}
                                                textVariant="small"
                                                // Our `small` face, as in the field.
                                                mono={false}
                                                className="flex-1"
                                            />
                                        </ComboboxItem>
                                    )
                                }
                            </ComboboxCollection>
                        </ComboboxList>
                    </ComboboxContent>
                </Combobox>
                <div className="flex min-h-4 items-center justify-between gap-3 md:min-h-5">
                    <div className="flex items-center gap-2">
                        <Text
                            id="squads-check"
                            variant="fine-print"
                            role="status"
                            className={cn(
                                "flex items-center gap-1",
                                status?.tone === "success"
                                    ? "text-success-base"
                                    : status?.tone === "error"
                                      ? "text-error-base"
                                      : "text-foreground-muted"
                            )}
                        >
                            {status?.tone === "success" && (
                                <CheckIcon
                                    aria-hidden="true"
                                    className="size-4 shrink-0"
                                />
                            )}
                            {status?.text}
                        </Text>
                        {canRetry && (
                            <Button
                                variant="link-error"
                                size="2xs"
                                className={STATUS_LINK_LABEL}
                                trailing={<RetryIcon />}
                                onClick={() =>
                                    draft
                                        ? setRetry((value) => value + 1)
                                        : account.retry()
                                }
                            >
                                Retry
                            </Button>
                        )}
                    </div>
                    {isAddedVault && (
                        <Button
                            variant="link"
                            size="2xs"
                            className={cn("shrink-0", STATUS_LINK_LABEL)}
                            aria-label="Remove multisig"
                            onClick={() =>
                                store.forget(
                                    account.signerAddress,
                                    account.address
                                )
                            }
                        >
                            Remove
                        </Button>
                    )}
                </div>
            </Field>
        </div>
    );
}
